import { ConstrainMode, DetailsListLayoutMode, DetailsRow, Dialog, DialogFooter, DialogType, FontIcon, IColumn, IDetailsListProps, IDialogContentProps, IStackItemStyles, Icon, IconButton, Label, PrimaryButton, SelectionMode, ShimmeredDetailsList, Stack, TooltipHost } from "@fluentui/react";
import React from "react";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import './ContentHome.css';
import { FolderTypeIconEnum } from "../../src/taskpane/components/enums/FolderTypeIconEnum";
import { ComponentTypes } from "../../src/taskpane/config/config";
import { FolderTypeIconQuery } from "../../src/taskpane/components/helpers/folderTypeIconQuery";
import { ImageTypeEnum } from "../../src/taskpane/components/enums/ImageTypeEnum";
import { getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import { ImageSizeEnum } from "../../src/taskpane/components/enums/ImageSizeEnum";
import { homeBrandIconStyles, homeFontIconStyles, homeFontIconStylesMedium, iconDisplayStyle } from "../../src/taskpane/styles/iconStyles";
import { FileTypeEnum } from "../../src/taskpane/components/enums/fileTypeEnum";
import { detailListStyles, detailsListClassNames } from "../Body/ContentBodyFilesFolders";
import FileOptionPanel from "../../src/taskpane/components/panels/FileOptionPanel";
import { IFilePanelInfo } from "../../src/taskpane/interfaces/IFilePanelInfo";
import { initialFileOptionPanelInfoSetting, initialFolderOptionPanelInfoSetting } from "../../src/taskpane/config/constants";
import FolderOptionPanel from "../../src/taskpane/components/panels/FolderOptionPanel";
import { IFolderPanelInfo } from "../../src/taskpane/interfaces/IFolderPanelInfo";
import { HomeItemType } from "./ContentManagerHome";
import { IStoreItem } from "../../src/taskpane/interfaces/IStoredItem";
import { setOneDriveDetails, setWorkingDirectories } from "../../src/taskpane/redux/modules/workingDirectory";
import { getOneDriveRootDetails } from "../../src/taskpane/services/GraphService";
import generalModule from "../../utilities/GeneralModule";
import { setContentManagerSearchValue, setIsContentManagerContentLoading, toggleContentManagerReload } from "../../src/taskpane/redux/modules/contentManagerDirectory";
import { logger } from "../../utilities/logger";
import { AccessLicenseHelper } from "../../src/taskpane/components/helpers/accessLicenseHelper";
import { getRecentItems, removeRecentItem } from "../../src/taskpane/redux/modules/recentItems";
import { removePinnedItem } from "../../src/taskpane/redux/modules/pinnedItems";
import { setShowFolderDeleteDialog } from "../../src/taskpane/redux/modules/showFolderDeleteDialogue";
import { ItemCheckFactory } from "../../src/taskpane/components/helpers/ItemCheck/ItemCheckFactory";
import { setLoading } from "../../src/taskpane/redux/modules/loader";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // import plugin
import { DraggableContainer } from "../../src/taskpane/components/_v2/components/Explorer/FolderView/DraggableContainer";
import { DragAndDropHelper, DragMode } from "../../utilities/DragAndDropHelper";
import { IOneDriveRootDetails } from "../../src/taskpane/interfaces/IOneDriveRootDetails";
import { IPageSettings } from "../../src/taskpane/interfaces/IPageSettings";

dayjs.extend(utc);

const gridFileNameStackItemStyles: IStackItemStyles = {
    root: {
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap"
    }
}

const gridOptionButtonStackItemStyles: IStackItemStyles = {
    root: {
        height: "100%",
        marginTop: -11,
        marginBottom: -11
    }
}

interface ContentHomeRecentProps {
    selectedHomeFilterType: HomeItemType
}

function copyAndSort<T>(items: T[], columnKey: string, isSortedDescending?: boolean): T[] {
    try {
        const key = columnKey as keyof T;
        const sortedItems = items.slice(0).sort((a: T, b: T) => {
            if (key === "lastModifiedDateTime") {
                if (a[key] === undefined) return 1;
                if (b[key] === undefined) return -1;
            }

            return ((isSortedDescending ? a[key] > b[key] : a[key] < b[key]) ? 1 : -1);
        });

        return sortedItems;
    } catch (error) {
        console.error("copyAndSort: ", error);
        return items;
    }
};

export const ContentHomeRecent = ({ selectedHomeFilterType }: ContentHomeRecentProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const dispatch = useDispatch();

    const recent = useSelector((state: RootState) => state.recent);
    const user = useSelector((state: RootState) => state.user);
    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);

    const [fileOptionPanelInfo, setFileOptionPanelInfo] = React.useState<IFilePanelInfo>(initialFileOptionPanelInfoSetting);
    const [folderOptionPanelInfo, setFolderOptionPanelInfo] = React.useState<IFolderPanelInfo>(initialFolderOptionPanelInfoSetting);
    const [licenseMessageContent, setLicenseMessageContent] = React.useState<IDialogContentProps>(null);
    const [isLicenseDialogHidden, setIsLicenseDialogHidden] = React.useState(true);
    const [items, setItems] = React.useState<IStoreItem[]>([]);

    const [columns, setColumns] = React.useState<IColumn[]>();
    const columnClickedRef = React.useRef<{ key: string, isSorted: boolean, isSortedDescending: boolean }>();

    React.useEffect(() => {
        if (recent && recent.recentItems === null && user?.account?.accountId) {
            dispatch(getRecentItems(user.account.accountId, languageStrings));
        }
    }, [recent, user?.account?.accountId]);

    React.useEffect(() => {
        if (recent && recent.recentItems && selectedHomeFilterType) {
            if (selectedHomeFilterType.extension) {
                setItems(recent.recentItems.filter(x => x.extension === selectedHomeFilterType.extension));
            } else if (selectedHomeFilterType.folderTypeIcon) {
                setItems(recent.recentItems.filter(x => x.folderTypeIcon === selectedHomeFilterType.folderTypeIcon));
            } else {
                setItems(recent?.recentItems || [])
            }
        } else {
            setItems([]);
        }
    }, [recent, selectedHomeFilterType]);

    const _onColumnClick = React.useCallback((_ev: React.MouseEvent<HTMLElement>, column: IColumn) => {
        try {
            setColumns(prevCols => {
                const newColumns = prevCols.slice();
                const currColumn: IColumn = newColumns.filter(currCol => column.key === currCol.key)[0];

                newColumns.forEach((newCol: IColumn) => {
                    if (newCol === currColumn) {
                        currColumn.isSortedDescending = !currColumn.isSortedDescending;
                        currColumn.isSorted = true;

                        columnClickedRef.current = {
                            key: currColumn.key,
                            isSorted: currColumn.isSorted,
                            isSortedDescending: currColumn.isSortedDescending
                        };
                    } else {
                        newCol.isSorted = false;
                        newCol.isSortedDescending = true;
                    }
                });

                setItems(prevFiltered => {
                    return copyAndSort(prevFiltered, currColumn.fieldName!, currColumn.isSortedDescending);
                });

                return newColumns;
            });
        } catch (error) {
            logger(error, "ContentHomeRecent (onColumnClick)");
        }
    }, [items, columns]);

    React.useEffect(() => {
        setColumns([
            {
                key: "icon",
                name: "",
                minWidth: 30,
                maxWidth: 30,
                isResizable: true,
                onRender(item) {
                    if (item.fileType === FileTypeEnum.file) {
                        return <TooltipHost content={item.filePath}>
                            <Icon
                                {...getFileTypeIconProps({
                                    extension: item.extension,
                                    size: 20,
                                    imageFileType: "svg"
                                })}
                                styles={iconDisplayStyle}
                            />
                        </TooltipHost>
                    } else {
                        const folderIcon = FolderTypeIconQuery.getImage(item.folderTypeIcon);
                        return folderIcon.imageType === ImageTypeEnum.fileType
                            ? <Icon {...getFileTypeIconProps({ type: folderIcon.fileIconType, size: 20, imageFileType: "svg" })} styles={iconDisplayStyle} />
                            : folderIcon.imageType === ImageTypeEnum.url
                                ? <img src={folderIcon.location} className={homeBrandIconStyles} width={20} height={20} />
                                : <FontIcon iconName={folderIcon.iconName} className={getFontIconClassName(folderIcon)} />;
                    }
                },
            },
            {
                key: "name",
                name: languageStrings.Name,
                fieldName: "name",
                minWidth: 150,
                maxWidth: 400,
                isRowHeader: true,
                isResizable: true,
                onRender(item) {
                    return (
                        <Stack horizontal verticalAlign="center">
                            <Stack.Item grow styles={gridFileNameStackItemStyles}>
                                <div style={{ cursor: "pointer" }} onClick={(e) => {
                                    if (item.fileType === FileTypeEnum.folder) {
                                        browseLocation(item, true);
                                    } else {
                                        const options = {
                                            driveId: item.driveId,
                                            fileType: item.extension,
                                            name: item.name,
                                            lastModifiedDateTime: item.lastModifiedDateTime,
                                            isOpen: true,
                                            itemId: item.itemId,
                                            folderId: item.folderId,
                                            isRecentItem: true,
                                            filePath: item.filePath,
                                            modifiedBy: item.modifiedBy,
                                            isFromHomePage: true,
                                            isFromPinnedItems: false,
                                            cannotbeRemoved: item?.cannotBeRemoved
                                        };
                                        setFileOptionPanelInfo(options);
                                    }
                                }}>
                                    {item.name}
                                </div>
                            </Stack.Item>
                            <Stack.Item styles={gridOptionButtonStackItemStyles}>
                                <TooltipHost content={languageStrings.MoreActions}>
                                    <IconButton
                                        aria-label="Options"
                                        className={detailsListClassNames.optionButton}
                                        iconProps={{ iconName: 'More' }}
                                        onClick={() => {
                                            if (item.fileType === FileTypeEnum.file) {
                                                const options = {
                                                    driveId: item.driveId,
                                                    fileType: item.extension,
                                                    name: item.name,
                                                    lastModifiedDateTime: item.lastModifiedDateTime,
                                                    isOpen: true,
                                                    itemId: item.itemId,
                                                    folderId: item.folderId,
                                                    isRecentItem: true,
                                                    filePath: item.filePath,
                                                    modifiedBy: item.modifiedBy,
                                                    isFromHomePage: true,
                                                    isFromPinnedItems: false,
                                                    cannotbeRemoved: item?.cannotBeRemoved
                                                };
                                                setFileOptionPanelInfo(options);
                                            } else {
                                                const options = {
                                                    isOpen: true,
                                                    folder: {
                                                        driveId: item.driveId,
                                                        id: item.itemId,
                                                        name: item.name,
                                                        sharePointListId: item.sharePointListId,
                                                        sharePointUniqueId: item.sharePointUniqueId
                                                    },
                                                    locations: item.locations,
                                                    webUrl: item.webUrl,
                                                    //saveToOneDrive: saveToOneDrive,
                                                    folderType: FolderTypeIconQuery.getFolderType(item.folderTypeIcon),
                                                    isARecentItem: true,
                                                    isFromHomePage: true,
                                                    isFromPinnedItems: false,
                                                    isTeamsSiteInsideSharepoint: item.isTeamsSiteInsideSharepoint
                                                }
                                                setFolderOptionPanelInfo(options);
                                            }
                                        }}
                                    />
                                </TooltipHost>
                            </Stack.Item>
                        </Stack>
                    )
                },
                isSorted: columnClickedRef.current && columnClickedRef.current.key === "name" ? columnClickedRef.current.isSorted : false,
                isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "name" ? columnClickedRef.current.isSortedDescending : false,
                onColumnClick: _onColumnClick
            },
            {
                key: "modified",
                name: languageStrings.Modified,
                fieldName: "lastModifiedDateTime",
                minWidth: 150,
                maxWidth: 160,
                isRowHeader: true,
                isResizable: true,
                onRender(item: IStoreItem) {
                    return item.lastModifiedDateTime ? dayjs.utc(item.lastModifiedDateTime).local().format("L LT") : "-";
                },
                isSorted: columnClickedRef.current && columnClickedRef.current.key === "modified" ? columnClickedRef.current.isSorted : false,
                isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "modified" ? columnClickedRef.current.isSortedDescending : false,
                onColumnClick: _onColumnClick
            },
            {
                key: "modifiedBy",
                name: languageStrings.ModifiedBy,
                fieldName: "modifiedBy",
                minWidth: 150,
                isRowHeader: true,
                isResizable: true,
                onRender(item, index, column) {
                    if (item.modifiedBy) {
                        return item.modifiedBy
                    }

                    return "-"
                },
                isSorted: columnClickedRef.current && columnClickedRef.current.key === "modifiedBy" ? columnClickedRef.current.isSorted : false,
                isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "modifiedBy" ? columnClickedRef.current.isSortedDescending : false,
                onColumnClick: _onColumnClick
            }
        ])
    }, []);

    const hideLicenseDialog = () => {
        setIsLicenseDialogHidden(true);
    }

    const showLicenseDialog = () => {
        setIsLicenseDialogHidden(false);
    }

    const browseLocation = async (homeItem: IStoreItem, isRecentList: boolean) => {
        setContextAwareLoading(true);
        try {
            if (await IsFolderInValidState(homeItem, isRecentList)) {
                let oneDriveDetails: IOneDriveRootDetails;
                const isOneDriveItem: boolean = homeItem.folderTypeIcon === FolderTypeIconEnum.oneDrive || homeItem.folderTypeIcon === FolderTypeIconEnum.oneDriveSubFolder;
                if (isOneDriveItem) {
                    if (!oneDriveRootDetails) {
                        oneDriveDetails = await getOneDriveRootDetails();
                        dispatch(setOneDriveDetails(oneDriveDetails));
                    }
                    else {
                        oneDriveDetails = oneDriveRootDetails;
                    }
                }

                if (!generalModule.isInConentMangerMode()) {
                    setContextAwareLoading(false);
                }

                if (Array.isArray(homeItem.locations) && homeItem.locations.length > 0) {
                    if (isOneDriveItem && oneDriveDetails?.sharePointListId) {
                        const homeItemLocations: IPageSettings[] = homeItem.locations.map(location => ({
                            ...location,
                            sharePointListId: oneDriveDetails.sharePointListId
                        }));
                        dispatch(setWorkingDirectories(homeItemLocations));
                    }
                    else {
                        dispatch(setWorkingDirectories(homeItem.locations));
                    }
                }

                if (generalModule.isInConentMangerMode()) {
                    dispatch(setContentManagerSearchValue(""));
                    dispatch(toggleContentManagerReload());
                    setContextAwareLoading(false);
                }
            }
            else {
                setContextAwareLoading(false);
            }
        } catch (error) {
            setContextAwareLoading(false);
            logger(error, "ContentHomeRecent.tsx (browseLocation)");
        }
    }

    const IsFolderInValidState = async (item: IStoreItem, isRecentList: boolean) => {
        let returnValue = true;
        try {

            let locationType = item.locations[0]?.type;

            if (!AccessLicenseHelper.canAccessItem(user.licenseType, locationType)) {
                const errorMessage = {
                    type: DialogType.largeHeader,
                    title: languageStrings.LicenseRequiredTitle,
                    closeButtonAriaLabel: "Close",
                    subText: languageStrings.LicenseRequired.replace("{placeholder}", locationType)
                };
                setLicenseMessageContent(errorMessage);
                showLicenseDialog();
                returnValue = false;
                return returnValue;
            }

            if (!user.settings.enableOneDrive || !user.settings.enableSharePoint || !user.settings.enableTeams) {
                if (!(locationType === ComponentTypes.OneDrive || locationType === ComponentTypes.OneDriveSubFolder)) {
                    let isTeams = false;
                    item.locations?.every(loc => {
                        if (loc.teamGroupId || loc.isTeamsRootFolder || loc.isTeamsFolder) {
                            isTeams = true;
                        }
                        return !isTeams;
                    })
                    locationType = isTeams ? ComponentTypes.Teams : locationType === ComponentTypes.SharePointList.toString() ? ComponentTypes.SharePointList : ComponentTypes.SharePoint;
                }
                const { canAccess, type } = AccessLicenseHelper.isLocationEnabled(locationType, user.settings);
                if (!canAccess) {
                    const errorMessage = {
                        type: DialogType.largeHeader,
                        title: languageStrings.AccessDenied,
                        closeButtonAriaLabel: "Close",
                        subText: languageStrings.AccessRevoked.replace("{placeholder}", type)
                    };
                    setLicenseMessageContent(errorMessage);
                    showLicenseDialog();
                    returnValue = false;
                    return returnValue;
                }
            }

            if (!(await CheckIfFolderExists(item))) {
                doFolderRemovalWork(isRecentList, item);
                returnValue = false;
            }
        }
        catch (e) {
            logger(e, "ContentHomePinned.tsx (isFolderInValidState)");
        }

        return returnValue;
    };

    const doFolderRemovalWork = (isRecentList: boolean, item: IStoreItem) => {
        const itemToRemove = { itemId: item.itemId, driveId: item.driveId };
        isRecentList
            ? dispatch(removeRecentItem(user.account.accountId, itemToRemove, FileTypeEnum.folder))
            : dispatch(removePinnedItem(user.account.accountId, itemToRemove, FileTypeEnum.folder));
        dispatch(setShowFolderDeleteDialog(true));
    };

    const CheckIfFolderExists = async (item: IStoreItem) => {
        const factory = new ItemCheckFactory(item.driveId, item.sharePointListId, item.siteId);
        const folderType = FolderTypeIconQuery.getFolderType(item.folderTypeIcon);
        const itemChecker = factory.createItemChecker(folderType, item?.isTeamsSiteInsideSharepoint);
        return await itemChecker.doesItemExist(item.itemId);
    };

    const setContextAwareLoading = React.useCallback((isLoading: boolean, contentManagerOnly: boolean = false) => {
        if (!contentManagerOnly) {
            !generalModule.isInConentMangerMode() ? dispatch(setLoading(isLoading)) : dispatch(setIsContentManagerContentLoading(isLoading));
        }
        else {
            dispatch(setIsContentManagerContentLoading(isLoading));
        }
    }, []);

    const licenseDialog = (
        <Dialog hidden={isLicenseDialogHidden} onDismiss={hideLicenseDialog} dialogContentProps={licenseMessageContent}>
            <DialogFooter>
                <PrimaryButton autoFocus onClick={hideLicenseDialog} text="Ok" />
            </DialogFooter>
        </Dialog>
    );

    const getFontIconClassName = (fontIcon): string => {
        if (fontIcon.imageSize === ImageSizeEnum.medium) {
            return homeFontIconStylesMedium;
        }
        return homeFontIconStyles;
    }

    const _onRenderRow: IDetailsListProps['onRenderRow'] = rowProps => {
        return (
            <DraggableContainer
                isValidDropZone={rowProps.item.fileType === FileTypeEnum.folder}
                onDropAfterFiles={(selectedGridItems?: any[]) => onGridItemDrop(rowProps.item, true, selectedGridItems)}
                onDragHover={() => onGridItemDrop(rowProps.item, false)}
                gridItem={rowProps.item}
            >
                <DetailsRow {...rowProps} className={detailsListClassNames.row} />
            </DraggableContainer>
        )
    }

    const dismissFileOptionPanel = React.useCallback(() => {
        setFileOptionPanelInfo(initialFileOptionPanelInfoSetting);
    }, []);

    const dismissFolderOptionPanel = React.useCallback(() => {
        setFolderOptionPanelInfo(initialFolderOptionPanelInfoSetting);
    }, []);

    const onGridItemDrop = React.useCallback(async (item, hasDropContent: boolean = true, selectedGridItems?: any[]) => {
        try {
            const inValidState = await IsFolderInValidState(item, true);
            if (await IsFolderInValidState(item, true)) {
                if (DragAndDropHelper.mode === DragMode.files || !hasDropContent) {
                    if (hasDropContent && DragAndDropHelper.mode === DragMode.files) {
                        generalModule.setOpenPanelUploadPanel(true);
                    }
                    dispatch(setWorkingDirectories(item.locations));
                    dispatch(setContentManagerSearchValue(""));
                    dispatch(toggleContentManagerReload());
                }
            }
        } catch (error) {
            logger(error, "ContentBodyFilesFolders.tsx (onGridItemDrop)");
        }
    }, [oneDriveRootDetails]);

    return (
        <>
            {recent && recent.recentItems && <ShimmeredDetailsList
                styles={detailListStyles}
                onRenderRow={_onRenderRow}
                compact={false}
                items={items}
                columns={columns?.length > 0 ? columns : []}
                selectionMode={SelectionMode.none}
                constrainMode={ConstrainMode.unconstrained}
                setKey="multiple"
                enableShimmer={recent?.isRecentItemsLoading}
                layoutMode={DetailsListLayoutMode.justified}
                ariaLabelForSelectionColumn="Toggle selection"
                ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                checkButtonAriaLabel="Row checkbox"
                className="xb-search-grid"
            />}
            {fileOptionPanelInfo?.isOpen && <FileOptionPanel info={fileOptionPanelInfo} dismissPanel={dismissFileOptionPanel} />}
            {folderOptionPanelInfo?.isOpen && <FolderOptionPanel info={folderOptionPanelInfo} dismissPanel={dismissFolderOptionPanel} />}
        </>
    )
};